import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useRouter } from 'next/router';
import AquillaButton from 'aquilla/core/molecules/Button';
import pushRoute from '@utils/pushRoute';
import { useSnackbar } from '@stores/snackbar';
import useUpscCurrentAffairsCaArticle from '@api/hooks/upscCurrentAffairs/useUpscCurrentAffairsCaArticle';
import useUpscCurrentAffairsHeroPages from '@api/hooks/upscCurrentAffairs/useUpscCurrentAffairsHeroPages';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import {
  trackUpscAnalysisCtaClicked,
  trackUpscQuizCtaClicked,
  trackUpscTopicShared
} from './upscCurrentAffairsAnalytics';
import getUpscHeroDateLabel from './upscCurrentAffairsUtils';

const HERO_TITLE_PREFIX = 'Current Affairs';
const TODAY_ANALYSIS_ROUTE = '/upsc-current-affairs/today-analysis';

const pulse = keyframes`
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
`;

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: 42px 16px 40px;
  background: linear-gradient(145deg, #0a2540 0%, #08aeea 55%, #1a73c8 100%);
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  max-width: 1136px;
  margin: 0 auto;
`;

const HeroLeft = styled.div`
  flex: 1;
  min-width: 260px;
`;

const Eyebrow = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin: 0 0 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-secondary-light);
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-i-green);
  flex-shrink: 0;
  animation: ${pulse} 1.4s infinite;
`;

const EyebrowDate = styled.span`
  color: var(--color-i-gold-1);
`;

const HeroTitle = styled.h1`
  margin: 0 0 24px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--color-i-white);

  ${mediaQuery.uptoMobile} {
    font-size: 22px;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SecondaryCta = styled(AquillaButton)`
  min-height: 40px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: ${Fonts.SMALL};
  font-weight: 600;
  background: transparent;
  border: 1.5px solid rgba(255, 255, 255, 0.28);
  color: var(--color-i-white);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--color-i-white);
  }
`;

const UpscCurrentAffairsDetailedPageHero = () => {
  const router = useRouter();
  const { id } = router.query;
  const articleId = typeof id === 'string' ? id : undefined;
  const { article } = useUpscCurrentAffairsCaArticle(
    router.isReady ? articleId : undefined
  );
  const { open: showSnackbar } = useSnackbar();
  const { heroPages } = useUpscCurrentAffairsHeroPages();
  const titleDate = getUpscHeroDateLabel();
  const cmsMainHeading =
    heroPages.detailPageHeroSession?.mainHeading?.trim() ?? '';
  const heroTitle = `${HERO_TITLE_PREFIX} ${titleDate}`;

  const handleGoBack = () => {
    trackUpscAnalysisCtaClicked("Back to Today's Analysis");
    pushRoute(TODAY_ANALYSIS_ROUTE);
  };

  const handleTakeQuiz = () => {
    trackUpscQuizCtaClicked('Take Quiz');
    pushRoute('/upsc-current-affairs/quiz');
  };

  const handleShare = () => {
    trackUpscTopicShared({
      topicId: article?.id,
      topicTitle: article?.topicHeading,
      topicDate: article?.publishAt,
      subjectName: article?.upscSubject?.heading
    });

    navigator.clipboard?.writeText(window.location.href);
    showSnackbar({
      message: 'Link copied successfully',
      type: 'success'
    });
  };

  return (
    <HeroSection aria-label="UPSC Current Affairs detailed edition">
      <HeroInner>
        <HeroLeft>
          <Eyebrow>
            <LiveDot aria-hidden />
            <span>UPSC Current Affairs</span>
            <span aria-hidden>·</span>
            <EyebrowDate>{titleDate}</EyebrowDate>
          </Eyebrow>
          <HeroTitle>
            {heroTitle}{' '}
            <span style={{ color: 'var(--color-i-gold-1)' }}>
              {cmsMainHeading || ''}
            </span>
          </HeroTitle>
          <HeroActions>
            <SecondaryCta type="button" onClick={handleGoBack}>
              ← Back to Today&apos;s Analysis
            </SecondaryCta>
            <SecondaryCta variant="secondary" onClick={handleTakeQuiz}>
              🎯 Take Quiz
            </SecondaryCta>
            <SecondaryCta
              variant="secondary"
              type="button"
              onClick={handleShare}
            >
              ↗ Share
            </SecondaryCta>
          </HeroActions>
        </HeroLeft>
      </HeroInner>
    </HeroSection>
  );
};

export default UpscCurrentAffairsDetailedPageHero;
